import React, { useMemo } from "react";
import { Badge } from "@/components/ui/badge";
import { Calendar, Monitor, Image, Clock, FileVideo, FileImage, AlertCircle, Info } from "lucide-react";
import CreativeDisplay from "./CreativeDisplay";
import { 
  getMediaOwnerDisplayName, 
  getResolutionBadgeColor, 
  getResolutionLabel,
  extractMediaOwnerFromUrl
} from "@/utils/creativeResolution";
import { format, addDays, parseISO } from "date-fns";
import { ar, enUS } from "date-fns/locale";
import { locations as allLocations } from "@/data/locations";

interface CreativeMetadata {
  url: string;
  locationName?: string;
  mediaOwner?: string;
  orientation?: 'portrait' | 'landscape';
  scheduledDays?: number[];
  resolution?: string;
}

interface GroupedCreativesDisplayProps {
  creativeUrls: string[];
  creativeMetadata?: CreativeMetadata[];
  language: 'en' | 'ar';
  startDate?: string;
  durationDays?: number;
  showGroupHeaders?: boolean;
  showScheduledDays?: boolean;
  showDetailedView?: boolean;
  selectedLocations?: any[];
}

// ============ METADATA EXTRACTION UTILITIES ============

/**
 * Extract scheduled days from URL pattern like `-day3-`
 */
const extractScheduledDaysFromUrl = (url: string): number[] => {
  try {
    const decodedUrl = decodeURIComponent(url).toLowerCase();
    const dayPattern = /[-_]day(\d+)[-_]/gi;
    const matches: number[] = [];
    let match;
    while ((match = dayPattern.exec(decodedUrl)) !== null) {
      const dayNum = parseInt(match[1], 10);
      if (dayNum > 0 && dayNum <= 365) {
        matches.push(dayNum);
      }
    }
    return matches;
  } catch {
    return [];
  }
};

/**
 * Extract orientation from URL patterns
 */
const extractOrientationFromUrl = (url: string): 'portrait' | 'landscape' | undefined => {
  try {
    const decodedUrl = decodeURIComponent(url).toLowerCase();
    if (decodedUrl.includes('-landscape-') || decodedUrl.includes('_landscape_') || decodedUrl.includes('landscape')) {
      return 'landscape';
    }
    if (decodedUrl.includes('-portrait-') || decodedUrl.includes('_portrait_') || decodedUrl.includes('portrait')) {
      return 'portrait';
    }
  } catch {}
  return undefined;
};

/**
 * Extract resolution from URL patterns
 */
const extractResolutionFromUrl = (url: string): string | undefined => {
  try {
    const decodedUrl = decodeURIComponent(url);
    const resPattern = /(\d{3,4})x(\d{3,4})/i;
    const match = decodedUrl.match(resPattern);
    if (match) {
      return `${match[1]}×${match[2]}`;
    }
  } catch {}
  return undefined;
};

/**
 * Extract location name from URL filename
 */
const extractLocationNameFromUrl = (url: string): string | undefined => {
  try {
    const decodedUrl = decodeURIComponent(url);
    const filename = decodedUrl.split('/').pop() || '';
    
    // Pattern: digits-LocationName-something
    const locationMatch = filename.match(/^\d+-(.+?)-[A-Za-z]/);
    if (locationMatch && locationMatch[1]) {
      let locationName = locationMatch[1]
        .replace(/_/g, ' ')
        .replace(/-/g, ' ')
        .trim();
      locationName = locationName.replace(/^\d+\s*[-_]?\s*/, '').trim();
      if (locationName && locationName.length > 2) {
        return locationName;
      }
    }
  } catch {}
  return undefined;
};

/**
 * Find location in master data or selected locations
 */
const findLocation = (name: string | undefined, selectedLocations?: any[]): any | undefined => {
  if (!name) return undefined;
  
  const normalizedName = name.toLowerCase().replace(/[^a-z0-9]/g, '');
  
  // Check selected locations first
  if (selectedLocations && selectedLocations.length > 0) {
    const found = selectedLocations.find(loc => {
      const locName = (loc.name || '').toLowerCase().replace(/[^a-z0-9]/g, '');
      return locName.includes(normalizedName) || normalizedName.includes(locName);
    });
    if (found) return found;
  }
  
  // Check master locations
  return allLocations.find(loc => {
    const locName = loc.name.toLowerCase().replace(/[^a-z0-9]/g, '');
    return locName.includes(normalizedName) || normalizedName.includes(locName);
  });
};

/**
 * Get metadata for a location
 */
const getLocationMetadata = (location: any): { orientation?: 'portrait' | 'landscape'; resolution?: string } => {
  if (!location) return {};
  
  const result: { orientation?: 'portrait' | 'landscape'; resolution?: string } = {};
  
  if (location.orientation) {
    result.orientation = location.orientation.toLowerCase() as 'portrait' | 'landscape';
  } else if (location.screenType) {
    const screenType = location.screenType.toLowerCase();
    if (screenType.includes('portrait')) {
      result.orientation = 'portrait';
    } else if (screenType.includes('landscape')) {
      result.orientation = 'landscape';
    }
  }
  
  if (location.details?.screenDimensions) {
    result.resolution = location.details.screenDimensions;
  }
  
  return result;
};

/**
 * Build complete metadata for a creative
 */
const buildCompleteMetadata = (
  url: string, 
  storedMeta: CreativeMetadata | undefined,
  selectedLocations?: any[]
): CreativeMetadata => {
  const result: CreativeMetadata = { url };
  
  // Start with stored metadata
  if (storedMeta) {
    result.locationName = storedMeta.locationName;
    result.mediaOwner = storedMeta.mediaOwner;
    result.orientation = storedMeta.orientation;
    result.resolution = storedMeta.resolution;
    result.scheduledDays = storedMeta.scheduledDays;
  }
  
  // Get location name from URL if not in stored metadata
  if (!result.locationName) {
    result.locationName = extractLocationNameFromUrl(url);
  }
  
  // Find matching location and get its metadata
  const matchedLocation = findLocation(result.locationName, selectedLocations);
  if (matchedLocation) {
    const locMeta = getLocationMetadata(matchedLocation);
    if (!result.orientation) result.orientation = locMeta.orientation;
    if (!result.resolution) result.resolution = locMeta.resolution;
    if (!result.mediaOwner) result.mediaOwner = matchedLocation.mediaOwner;
  }
  
  // Fall back to URL pattern extraction
  if (!result.orientation) result.orientation = extractOrientationFromUrl(url);
  if (!result.resolution) result.resolution = extractResolutionFromUrl(url);
  if (!result.scheduledDays || result.scheduledDays.length === 0) {
    result.scheduledDays = extractScheduledDaysFromUrl(url);
  }
  if (!result.mediaOwner) result.mediaOwner = extractMediaOwnerFromUrl(url) || undefined;
  
  return result;
};

// ============ MAIN COMPONENT ============

const GroupedCreativesDisplay: React.FC<GroupedCreativesDisplayProps> = ({
  creativeUrls,
  creativeMetadata,
  language,
  startDate,
  durationDays,
  showGroupHeaders = true,
  showScheduledDays = true,
  showDetailedView = true,
  selectedLocations
}) => {
  const locale = language === 'ar' ? ar : enUS;
  
  if (!creativeUrls || creativeUrls.length === 0) {
    return (
      <div className="text-center py-8 text-muted-foreground">
        {language === 'ar' ? 'لا توجد إعلانات مرفقة' : 'No creatives uploaded'}
      </div>
    );
  }

  // Normalize URL for matching (handle encoding differences)
  const normalizeUrlForMatch = (url: string): string => {
    try {
      // Decode and re-encode to normalize, strip query params
      let decoded = decodeURIComponent(url);
      decoded = decoded.split('?')[0].split('#')[0];
      return decoded;
    } catch {
      return url.split('?')[0].split('#')[0];
    }
  };

  // Extract filename from URL for fallback matching
  const extractFilenameFromUrl = (url: string): string => {
    try {
      const decoded = decodeURIComponent(url);
      const path = decoded.split('?')[0].split('#')[0];
      return (path.split('/').pop() || '').toLowerCase();
    } catch {
      const path = url.split('?')[0].split('#')[0];
      return (path.split('/').pop() || '').toLowerCase();
    }
  };

  // Build complete metadata map for all URLs
  const metadataMap = useMemo(() => {
    const map = new Map<string, CreativeMetadata>();
    
    // Create a lookup from stored metadata with normalized keys
    const storedMap = new Map<string, CreativeMetadata>();
    const filenameMap = new Map<string, CreativeMetadata>();
    
    if (creativeMetadata && creativeMetadata.length > 0) {
      for (const meta of creativeMetadata) {
        if (meta.url) {
          // Store with both original and normalized URL as keys
          storedMap.set(meta.url, meta);
          storedMap.set(normalizeUrlForMatch(meta.url), meta);
          // Also store by filename for extra fallback
          const filename = extractFilenameFromUrl(meta.url);
          if (filename) {
            filenameMap.set(filename, meta);
          }
        }
      }
    }
    
    // Build complete metadata for each URL
    for (const url of creativeUrls) {
      // Try exact match first
      let storedMeta = storedMap.get(url);
      
      // Try normalized match
      if (!storedMeta) {
        storedMeta = storedMap.get(normalizeUrlForMatch(url));
      }
      
      // Try filename match
      if (!storedMeta) {
        const filename = extractFilenameFromUrl(url);
        if (filename) {
          storedMeta = filenameMap.get(filename);
        }
      }
      
      const completeMeta = buildCompleteMetadata(url, storedMeta, selectedLocations);
      map.set(url, completeMeta);
    }
    
    return map;
  }, [creativeUrls, creativeMetadata, selectedLocations]);

  // Group creatives by media owner
  const groupCreatives = (): Record<string, { url: string; metadata: CreativeMetadata }[]> => {
    const grouped: Record<string, { url: string; metadata: CreativeMetadata }[]> = {};
    
    for (const url of creativeUrls) {
      const metadata = metadataMap.get(url)!;
      let key = metadata.locationName || metadata.mediaOwner || extractMediaOwnerFromUrl(url) || 'Other';
      
      // Normalize key for grouping
      const baseKey = key.replace(/-landscape|-portrait/g, '');
      const orientation = metadata.orientation;
      
      // For Alan, group by orientation
      if (baseKey === 'Alan' && orientation) {
        key = `Alan-${orientation}`;
      } else {
        key = baseKey;
      }
      
      if (!grouped[key]) {
        grouped[key] = [];
      }
      grouped[key].push({ url, metadata });
    }
    
    return grouped;
  };

  const groupedCreatives = groupCreatives();
  const groupKeys = Object.keys(groupedCreatives);

  const isVideo = (url: string): boolean => {
    const videoExtensions = ['.mp4', '.webm', '.mov', '.avi', '.mkv'];
    return videoExtensions.some(ext => url.toLowerCase().includes(ext));
  };

  // Format scheduled days display (more robust date parsing + more detail)
  const formatScheduledDays = (days: number[] | undefined): { short: string; full: string; hasDays: boolean } => {
    if (!days || days.length === 0) {
      return {
        short: language === "ar" ? "غير محدد" : "Not set",
        full: language === "ar" ? "لم يتم تحديد الأيام" : "No days selected",
        hasDays: false,
      };
    }

    // Sort days for consistent display
    const sortedDays = [...days]
      .map((d) => Number(d))
      .filter((d) => Number.isFinite(d) && d > 0)
      .sort((a, b) => a - b);

    const dayLabel = (d: number) => (language === "ar" ? `اليوم ${d}` : `Day ${d}`);

    // If we don't have a valid startDate, fall back to day numbers
    if (!startDate) {
      const daysStr = sortedDays.map(dayLabel).join(" • ");
      return { short: daysStr, full: daysStr, hasDays: true };
    }

    // Robust start date parsing (handles YYYY-MM-DD, ISO with time, and DB strings with spaces)
    const parseStartDateSafe = (raw: string): Date | null => {
      const cleaned = String(raw || "").trim();
      if (!cleaned) return null;

      // If it's a date-only string, set midday to avoid timezone edge cases.
      const normalized = /^\d{4}-\d{2}-\d{2}$/.test(cleaned)
        ? `${cleaned}T12:00:00`
        : cleaned.includes(" ") && !cleaned.includes("T")
          ? cleaned.replace(" ", "T")
          : cleaned;

      try {
        const d = parseISO(normalized);
        if (Number.isFinite(d.getTime())) return d;
      } catch {
        // ignore
      }

      const fallback = new Date(cleaned);
      return Number.isFinite(fallback.getTime()) ? fallback : null;
    };

    const baseDate = parseStartDateSafe(startDate);
    if (!baseDate) {
      const daysStr = sortedDays.map(dayLabel).join(" • ");
      return { short: daysStr, full: daysStr, hasDays: true };
    }

    // Build detailed labels: "Day 1 (Sun, Jan 18, 2026)"
    const detailed = sortedDays.map((day) => {
      const date = addDays(baseDate, day - 1);
      const dateLabel = format(date, "EEE, MMM d, yyyy", { locale });
      return language === "ar" ? `اليوم ${day} (${dateLabel})` : `Day ${day} (${dateLabel})`;
    });

    const full = detailed.join(" • ");

    // Keep the on-card preview concise; full list remains available via title tooltip.
    const previewCount = 3;
    const short =
      detailed.length <= previewCount
        ? full
        : `${detailed.slice(0, previewCount).join(" • ")} +${detailed.length - previewCount} ${language === "ar" ? "أيام أخرى" : "more"}`;

    return { short, full, hasDays: true };
  };

  // Get orientation display label
  const getOrientationLabel = (orientation?: string): { label: string; hasValue: boolean } => {
    if (!orientation) return { label: language === 'ar' ? 'غير محدد' : 'Not specified', hasValue: false };
    if (orientation === 'landscape') return { label: language === 'ar' ? 'أفقي' : 'Landscape', hasValue: true };
    if (orientation === 'portrait') return { label: language === 'ar' ? 'عمودي' : 'Portrait', hasValue: true };
    return { label: orientation, hasValue: true };
  };

  // Get resolution display value
  const getResolutionDisplay = (resolution?: string): { label: string; hasValue: boolean } => {
    if (!resolution) return { label: language === 'ar' ? 'غير محدد' : 'Not specified', hasValue: false };
    return { label: resolution, hasValue: true };
  };

  // Simplified grid for single "Other" group
  if (groupKeys.length === 1 && groupKeys[0] === 'Other' && !showDetailedView) {
    return (
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
        {creativeUrls.map((url, idx) => (
          <CreativeDisplay
            key={idx}
            url={url}
            index={idx}
            getResolutionBadgeColor={getResolutionBadgeColor}
            getResolutionLabel={getResolutionLabel}
            showBadge={false}
          />
        ))}
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {groupKeys.map((mediaOwnerKey) => {
        const items = groupedCreatives[mediaOwnerKey];
        const displayName = getMediaOwnerDisplayName(mediaOwnerKey);
        const badgeColor = items[0]?.url ? getResolutionBadgeColor(items[0].url) : 'bg-muted text-muted-foreground';
        
        // Only show scheduled days if this location/group has 2+ creatives
        const hasMultipleCreatives = items.length >= 2;
        
        return (
          <div key={mediaOwnerKey} className="space-y-3">
            {showGroupHeaders && (
              <div className="flex items-center gap-2 flex-wrap">
                <Badge variant="outline" className={`${badgeColor} font-medium`}>
                  {displayName}
                </Badge>
                <span className="text-sm text-muted-foreground">
                  ({items.length} {items.length === 1 
                    ? (language === 'ar' ? 'ملف' : 'file') 
                    : (language === 'ar' ? 'ملفات' : 'files')
                  })
                </span>
              </div>
            )}
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
              {items.map(({ url, metadata }, idx) => {
                const scheduledInfo = formatScheduledDays(metadata.scheduledDays);
                const orientationInfo = getOrientationLabel(metadata.orientation);
                const resolutionInfo = getResolutionDisplay(metadata.resolution);
                const isVideoFile = isVideo(url);
                
                return (
                  <div key={`${mediaOwnerKey}-${idx}`} className="border rounded-lg overflow-hidden bg-card">
                    {/* Creative preview */}
                    <div className="relative aspect-video bg-muted">
                      <CreativeDisplay
                        url={url}
                        index={idx}
                        getResolutionBadgeColor={getResolutionBadgeColor}
                        getResolutionLabel={getResolutionLabel}
                        showBadge={false}
                      />
                      <div className="absolute top-2 right-2">
                        <Badge variant="secondary" className="bg-black/50 text-white text-xs">
                          {isVideoFile ? (
                            <><FileVideo className="h-3 w-3 mr-1" /> Video</>
                          ) : (
                            <><FileImage className="h-3 w-3 mr-1" /> Image</>
                          )}
                        </Badge>
                      </div>
                    </div>
                    
                    {/* Creative details */}
                    {showDetailedView && (
                      <div className="p-3 space-y-2 text-sm">
                        {/* Orientation */}
                        <div className="flex items-center justify-between">
                          <div className="flex items-center gap-2 text-muted-foreground">
                            <Monitor className="h-4 w-4" />
                            <span>{language === 'ar' ? 'الاتجاه' : 'Orientation'}</span>
                          </div>
                          <Badge 
                            variant="outline" 
                            className={`text-xs ${orientationInfo.hasValue ? '' : 'bg-amber-50 text-amber-600 border-amber-200'}`}
                          >
                            {!orientationInfo.hasValue && <Info className="h-3 w-3 mr-1" />}
                            {orientationInfo.label}
                          </Badge>
                        </div>
                        
                        {/* Resolution */}
                        <div className="flex items-center justify-between">
                          <div className="flex items-center gap-2 text-muted-foreground">
                            <Image className="h-4 w-4" />
                            <span>{language === 'ar' ? 'الدقة' : 'Resolution'}</span>
                          </div>
                          <span className={`font-medium ${resolutionInfo.hasValue ? '' : 'text-amber-600'}`}>
                            {!resolutionInfo.hasValue && <Info className="h-3 w-3 inline mr-1" />}
                            {resolutionInfo.label}
                          </span>
                        </div>
                        
                        {/* Scheduled days - always show when showScheduledDays is true */}
                        {showScheduledDays && (
                          <div className="flex items-start justify-between">
                            <div className="flex items-center gap-2 text-muted-foreground">
                              <Calendar className="h-4 w-4" />
                              <span>{language === 'ar' ? 'الأيام' : 'Scheduled'}</span>
                            </div>
                            <div className="text-right">
                              {scheduledInfo.hasDays ? (
                                <div>
                                  <Badge variant="secondary" className="text-xs bg-primary/10 text-primary border-primary/20">
                                    <Clock className="h-3 w-3 mr-1" />
                                    {metadata.scheduledDays?.length || 0} {language === 'ar' ? 'يوم' : (metadata.scheduledDays?.length === 1 ? 'day' : 'days')}
                                  </Badge>
                                  <p className="text-xs text-muted-foreground mt-1 max-w-[180px]" title={scheduledInfo.full}>
                                    {scheduledInfo.short}
                                  </p>
                                </div>
                              ) : hasMultipleCreatives ? (
                                <Badge variant="outline" className="text-xs bg-amber-50 text-amber-700 border-amber-200">
                                  <AlertCircle className="h-3 w-3 mr-1" />
                                  {language === 'ar' ? 'لم يتم تحديد الأيام' : 'Days not set'}
                                </Badge>
                              ) : (
                                <Badge variant="outline" className="text-xs bg-muted text-muted-foreground">
                                  {language === 'ar' ? 'جميع الأيام' : 'All days'}
                                </Badge>
                              )}
                            </div>
                          </div>
                        )}
                        
                        {/* Location name if available */}
                        {metadata.locationName && (
                          <div className="pt-1 border-t">
                            <p className="text-xs text-muted-foreground truncate" title={metadata.locationName}>
                              📍 {metadata.locationName}
                            </p>
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        );
      })}
    </div>
  );
};

export default GroupedCreativesDisplay;
